import { call, put, take, takeLatest } from "redux-saga/effects";
import { httpDelete, httpGet, httpPost } from "../../services";

import { showLoadingAction } from "../actions/appStateActions";
import {
  deleteSegmentErrorAction,
  deleteSegmentLoadingAction,
  DeleteSegmentSchema,
  deleteSegmentSuccessAction,
  getAllCustomObjectsErrorAction,
  getAllCustomObjectsLoadingAction,
  GetAllCustomObjectsSchema,
  getAllCustomObjectsSuccessAction,
  getCustomFieldsErrorAction,
  getCustomFieldsLoadingAction,
  GetCustomFieldsSchema,
  getCustomFieldsSuccessAction,
  getCustomObjTopTenUsersErrorAction,
  getCustomObjTopTenUsersLoadingAction,
  getCustomObjTopTenUsersSuccessAction,
  getCustomObjUsersErrorAction,
  getCustomObjUsersLoadingAction,
  GetCustomObjUsersSchema,
  getCustomObjUsersSuccessAction,
  getSavedSegmentsErrorAction,
  getSavedSegmentsLoadingAction,
  GetSavedSegmentsSchema,
  getSavedSegmentsSuccessAction,
  GetSegmentationsUsersQueryParameters,
  GetTopTenResponseSchema,
  getTopTenUsersErrorAction,
  getTopTenUsersLoadingAction,
  getTopTenUsersSuccessAction,
  postUserSegmentationQueryErrorAction,
  postUserSegmentationQueryLoadingAction,
  PostUserSegmentationQueryResponseSchema,
  postUserSegmentationQuerySuccessAction,
  SegmentationConst
} from "../actions/segmentationsAction";

/**
 * Workers
 */
function* getCustomFieldsHandler() {
  try {
    yield put(getCustomFieldsLoadingAction());

    const data: GetCustomFieldsSchema = yield httpGet("/segmentations/customfields");
    if (data.errorCode) {
      throw new Error(data.message);
    }
    yield put(getCustomFieldsSuccessAction(data));
  } catch (e: any) {
    yield put(getCustomFieldsErrorAction(e.message));
  }
}

function* postUserSegmentationQuery(filter: any, queryParameters: GetSegmentationsUsersQueryParameters) {
  try {
    yield put(postUserSegmentationQueryLoadingAction());
    const response: PostUserSegmentationQueryResponseSchema = yield httpPost("/segmentations/users", queryParameters, { filter });
    if (response.ErrorCode) {
      throw Error(response.Message);
    }
    yield put(postUserSegmentationQuerySuccessAction({ ...response }));
  } catch (e: any) {
    yield put(postUserSegmentationQueryErrorAction(e.message));
  }
}

function* getSegmentationsTopTenUsers() {
  try {
    yield put(getTopTenUsersLoadingAction());
    const response: GetTopTenResponseSchema = yield httpGet("/segmentations/users/topten");
    if (response.status !== undefined) {
      if (response.status === 404) {
        response.totalUsers = 0;
      } else throw Error("Invalid Query.");
    }
    yield put(getTopTenUsersSuccessAction({ response }));
  } catch (e: any) {
    yield put(getTopTenUsersErrorAction(e.message));
  }
}

function* getSavedSegmentsHandler() {
  try {
    yield put(getSavedSegmentsLoadingAction());
    const data: GetSavedSegmentsSchema = yield httpGet("/segmentations/");
    if (data.ErrorCode) {
      throw new Error(data.Description || data.Message);
    }
    yield put(
      getSavedSegmentsSuccessAction({
        ...data,
        segmentations: data.segmentations.slice().sort((a, b) => new Date(b.DateCreated).getTime() - new Date(a.DateCreated).getTime())
      })
    );
  } catch (e: any) {
    yield put(getSavedSegmentsErrorAction(e.message));
  }
}

function* deleteSegmentHandler(token: string) {
  try {
    yield put(deleteSegmentLoadingAction());
    const data: DeleteSegmentSchema = yield httpDelete("/segmentations/", { id: token }, {});
    yield put(deleteSegmentSuccessAction(data));
  } catch (e: any) {
    yield put(deleteSegmentErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* getAllCustomObjectsHandler() {
  try {
    yield put(getAllCustomObjectsLoadingAction());
    const data: GetAllCustomObjectsSchema = yield httpGet("/segmentations/customobject/all");
    if (data.errorCode || data.ErrorCode) {
      throw new Error(data.Message || "");
    }
    yield put(getAllCustomObjectsSuccessAction(data));
  } catch (e: any) {
    yield put(getAllCustomObjectsErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* getCustomObjTopTenUsersHandler(customObject: string) {
  try {
    yield put(getCustomObjTopTenUsersLoadingAction());
    const data: GetCustomObjUsersSchema = yield httpGet("/segmentations/customobject/topten", { customObject });
    if (data.errorCode || data.status || data.ErrorCode) {
      if (data.status === 404) {
        data.totalUsers = 0;
      } else throw new Error(data.Message || "");
    }
    yield put(getCustomObjTopTenUsersSuccessAction(data));
  } catch (e: any) {
    yield put(getCustomObjTopTenUsersErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* getCustomObjUsersHandler(filter: any, name: string, queryParameters: GetSegmentationsUsersQueryParameters) {
  try {
    yield put(getCustomObjUsersLoadingAction());
    const data: GetCustomObjUsersSchema = yield httpPost("/segmentations/customobject", queryParameters, {
      filter,
      name
    });
    if (data.errorCode || data.ErrorCode) {
      throw Error(data.Message || data.message || "");
    }
    yield put(getCustomObjUsersSuccessAction(data));
  } catch (e: any) {
    yield put(getCustomObjUsersErrorAction(e.message));
  }
}

/**
 * Watchers
 */
export function* watchCustomFields() {
  while (true) {
    yield take(SegmentationConst.GET_CUSTOMFIELDS);
    yield call(getCustomFieldsHandler);
  }
}

export function* watchPostUserSegmentationQuery() {
  // while (true) {
  //   const {
  //     payload: { filter, queryParameters }
  //   } = yield take(SegmentationConst.POST_USER_SEGMENTATION_QUERY);
  //   yield call(postUserSegmentationQuery, filter, queryParameters);
  // }
  //need to check again
  yield takeLatest(SegmentationConst.POST_USER_SEGMENTATION_QUERY, ({ payload: { filter, queryParameters } }: any) => postUserSegmentationQuery(filter, queryParameters));
}

export function* watchGetSegmentationsTopTenUsers() {
  while (true) {
    yield take(SegmentationConst.GET_SEGMENTATIONS_TOP_TEN_USERS);
    yield call(getSegmentationsTopTenUsers);
  }
}

export function* watchSavedSegments() {
  while (true) {
    yield take(SegmentationConst.GET_SAVED_SEGMENTS);
    yield call(getSavedSegmentsHandler);
  }
}

export function* watchDeleteSegment() {
  while (true) {
    const {
      payload: { id }
    } = yield take(SegmentationConst.DELETE_SEGMENT);
    yield call(deleteSegmentHandler, id);
  }
}

export function* watchGetAllCustomObjects() {
  while (true) {
    yield take(SegmentationConst.GET_ALL_CUSTOM_OBJECTS);
    yield call(getAllCustomObjectsHandler);
  }
}

export function* watchGetCustomObjTopTenUsers() {
  while (true) {
    const {
      payload: { customObject }
    } = yield take(SegmentationConst.GET_CUSTOMOBJ_TOPTEN_USERS);
    yield call(getCustomObjTopTenUsersHandler, customObject);
  }
}

export function* watchGetCustomObjUsers() {
  while (true) {
    const {
      payload: { filter, name, queryParameters }
    } = yield take(SegmentationConst.GET_CUSTOMOBJ_USERS);
    yield call(getCustomObjUsersHandler, filter, name, queryParameters);
  }
}
